import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Upload, message, Image, Space, Popconfirm 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import API_ENDPOINTS from '../config/api';

const { TextArea } = Input;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [pdfUrl, setPdfUrl] = useState(''); // New state for PDF URL
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CATEGORIES);
      const result = await response.json();
      if (result.status === 'success') {
        setCategories(result.data);
      }
    } catch (error) {
      message.error('Failed to fetch categories');
    }
  };

  const handleAddNew = () => {
    setEditingCategory(null);
    setImageUrl('');
    setPdfUrl(''); // Reset PDF URL
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    setImageUrl(record.image_url || '');
    setPdfUrl(record.pdf_url || ''); // Set PDF URL for editing
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      slug: record.slug,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CATEGORIES}/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.status === 'success') {
        message.success('Category deleted successfully');
        fetchCategories();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to delete category');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append basic category data
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      if (values.slug) {
        formData.append('slug', values.slug);
      }

      // Handle image upload
      if (values.image && values.image[0]) {
        const file = values.image[0].originFileObj;
        formData.append('image', file);
      }

      // Handle PDF upload
      if (values.pdf && values.pdf[0]) {
        const file = values.pdf[0].originFileObj;
        formData.append('pdf', file);
      }

      let url = API_ENDPOINTS.CATEGORIES;
      let method = 'POST';

      // If editing, adjust the URL and method
      if (editingCategory) {
        url = `${API_ENDPOINTS.CATEGORIES}/${editingCategory.id}`;
        method = 'POST';
        formData.append('_method', 'PUT');
        formData.append('id', editingCategory.id.toString());
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to save category');
      }
      
      if (result.status === 'success') {
        message.success(editingCategory ? 'Category updated successfully' : 'Category created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setImageUrl('');
        setPdfUrl(''); // Reset PDF URL
        fetchCategories();
      } else {
        throw new Error(result.message || 'Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      message.error(error.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image',
      render: (image_url) => (
        <Image
          src={image_url || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMAAAADA...'}
          alt="Category"
          width={50}
          height={50}
          style={{ objectFit: 'cover' }}
          onError={(e) => {
            e.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMAAAADA...';
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'PDF',
      dataIndex: 'pdf_url',
      key: 'pdf',
      render: (pdf_url) =>
        pdf_url ? (
          <a href={pdf_url} target="_blank" rel="noopener noreferrer">
            View PDF
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <h1>Categories</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
          Add New Category
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={categories}
        rowKey="id"
      />

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please input category name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug"
          >
            <Input placeholder="Leave blank to auto-generate" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="image"
            label="Category Image"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
              onChange={({ fileList }) => {
                if (fileList.length > 0 && fileList[0].originFileObj) {
                  const url = URL.createObjectURL(fileList[0].originFileObj);
                  setImageUrl(url);
                } else {
                  setImageUrl(editingCategory?.image_url || '');
                }
              }}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="category" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload Image</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="pdf"
            label="Category PDF"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload
              listType="text"
              maxCount={1}
              beforeUpload={() => false}
              accept="application/pdf"
              onChange={({ fileList }) => {
                if (fileList.length > 0 && fileList[0].originFileObj) {
                  const url = URL.createObjectURL(fileList[0].originFileObj);
                  setPdfUrl(url);
                } else {
                  setPdfUrl(editingCategory?.pdf_url || '');
                }
              }}
            >
              <Button icon={<UploadOutlined />}>
                {pdfUrl ? 'Replace PDF' : 'Upload PDF'}
              </Button>
            </Upload>
          </Form.Item>

          {pdfUrl && (
            <div style={{ marginBottom: 16 }}>
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                View Current PDF
              </a>
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;